/**
 * Iter 20.23 — Strukturierte Rückfrage-Component
 *
 * Rendert eine LLM-Rückfrage mit dem passenden Antwort-Widget:
 *   - yes_no             → Chip-Buttons Ja / Nein / Weiß ich nicht
 *   - single_choice      → Chip-Auswahl aus vom LLM vorgegebenen Optionen
 *   - number_with_unit   → Zahleneingabe mit Einheit
 *   - short_text         → einzeilige Eingabe
 *   - long_text          → mehrzeilige Textarea
 *
 * Zusätzlich:
 *   - Impact-Badge („Wichtigste Frage" / „Nuance") je nach LLM-Rating
 *   - „Warum diese Frage?"-Expander mit LLM-Begründung
 *
 * Der Value ist immer ein String (für Rückwärts-Kompat mit dem
 * bestehenden Q&A-Reanalyze-Flow, der Q + A als Fließtext ans LLM schickt).
 */
import React, { useState } from "react";
import { CaretRight, Info, Star, Plus, X } from "@phosphor-icons/react";
import { splitAnswerValue as splitValue, joinAnswerValue as joinValue } from "./answerValue";

const IMPACT_LABEL = {
  high: "Wichtigste Frage",
  medium: "Hilft der Auswertung",
  low: "Feine Nuance",
};

const IMPACT_STYLE = {
  high: "bg-amber-100 text-amber-800 border-amber-200",
  medium: "bg-warm-100 text-warm-700 border-warm-200",
  low: "bg-ink-50 text-ink-500 border-ink-200",
};

const YES_NO_OPTIONS = [
  { value: "Ja", label: "Ja" },
  { value: "Nein", label: "Nein" },
  { value: "Weiß ich nicht", label: "Weiß ich nicht" },
];

function ChipButton({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-selected={selected ? "true" : "false"}
      className={[
        "px-3 py-1.5 text-sm rounded-full border transition-colors",
        "text-left leading-snug break-words whitespace-normal max-w-full",
        selected
          ? "bg-brand-500 border-brand-500 text-white shadow-sm"
          : "bg-white border-ink-300 text-ink-700 hover:border-brand-400 hover:bg-warm-50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function ClarifyingQuestion({
  index,
  question,     // structured object: { question, type, options, unit, placeholder, impact, impact_reason }
  value,        // current answer as string
  onChange,     // (newValue: string) => void
  autoExpand,   // if false, hide low-impact by default (parent handles)
}) {
  const [whyOpen, setWhyOpen] = useState(false);

  const q = question || {};
  const questionText = q.question || "";
  const type = q.type || "short_text";
  const impact = q.impact || "medium";
  const impactReason = q.impact_reason || "";

  const impactStyle = IMPACT_STYLE[impact] || IMPACT_STYLE.medium;
  const showImpactBadge = impact === "high" || impact === "low";

  return (
    <li
      className="border border-ink-200 bg-white rounded-sm p-4"
      data-testid={`tool-question-${index}`}
      data-impact={impact}
      data-type={type}
    >
      <div className="flex items-start gap-3">
        <CaretRight size={16} weight="bold" className="text-brand-500 mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          {/* Frage-Header: Text + Impact-Badge + „Warum?"-Toggle */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="text-sm text-ink-900 leading-relaxed flex-1 min-w-0">
              {questionText}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {showImpactBadge && (
                <span
                  className={[
                    "inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold rounded-full border",
                    impactStyle,
                  ].join(" ")}
                  data-testid={`tool-question-${index}-impact`}
                  title={impact === "high" ? "Trennt am stärksten zwischen zwei Gruppen" : "Feine Nuance, verfeinert Ergebnis"}
                >
                  {impact === "high" && <Star size={10} weight="fill" />}
                  {IMPACT_LABEL[impact]}
                </span>
              )}
              {impactReason && (
                <button
                  type="button"
                  onClick={() => setWhyOpen((v) => !v)}
                  className="text-[11px] text-warm-500 hover:text-brand-600 hover:underline whitespace-nowrap"
                  data-testid={`tool-question-${index}-why-toggle`}
                >
                  <Info size={12} weight="regular" className="inline mr-0.5 -mt-0.5" />
                  Warum?
                </button>
              )}
            </div>
          </div>

          {whyOpen && impactReason && (
            <div
              className="mb-2 rounded-sm bg-warm-50 border border-warm-200 px-3 py-2 text-[12px] text-ink-700 leading-snug"
              data-testid={`tool-question-${index}-why-body`}
            >
              {impactReason}
            </div>
          )}

          {/* Antwort-Widget je nach type */}
          <QuestionInput
            index={index}
            type={type}
            options={q.options || []}
            unit={q.unit || ""}
            placeholder={q.placeholder || ""}
            value={value || ""}
            onChange={onChange}
          />

          {/* Iter 20.24 — Optionale Freitext-Ergänzung für Chip-basierte
              Fragen (yes_no + single_choice). Kollabiert per Default; User
              klickt „Antwort ergänzen …" um eine kleine Textarea zu öffnen. */}
          {(type === "yes_no" || type === "single_choice") && (
            <FreeTextAppendix
              index={index}
              value={value || ""}
              onChange={onChange}
            />
          )}
        </div>
      </div>
    </li>
  );
}

function QuestionInput({ index, type, options, unit, placeholder, value, onChange }) {
  const { main, note } = splitValue(value);

  if (type === "yes_no") {
    return (
      <div className="flex flex-wrap gap-2" data-testid={`tool-answer-${index}-chips`}>
        {YES_NO_OPTIONS.map((opt) => (
          <ChipButton
            key={opt.value}
            selected={main === opt.value}
            onClick={() => onChange(joinValue(main === opt.value ? "" : opt.value, note))}
            testId={`tool-answer-${index}-${opt.value.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {opt.label}
          </ChipButton>
        ))}
      </div>
    );
  }

  if (type === "single_choice") {
    return (
      <div className="flex flex-wrap gap-2" data-testid={`tool-answer-${index}-chips`}>
        {options.map((opt, i) => (
          <ChipButton
            key={i}
            selected={main === opt}
            onClick={() => onChange(joinValue(main === opt ? "" : opt, note))}
            testId={`tool-answer-${index}-opt-${i}`}
          >
            {opt}
          </ChipButton>
        ))}
        <ChipButton
          selected={main === "Weiß ich nicht"}
          onClick={() => onChange(joinValue(main === "Weiß ich nicht" ? "" : "Weiß ich nicht", note))}
          testId={`tool-answer-${index}-unknown`}
        >
          Weiß ich nicht
        </ChipButton>
      </div>
    );
  }

  if (type === "number_with_unit") {
    // Wir speichern als String, damit der Reanalyze-Flow unverändert bleibt.
    // Ziffer + optionale Einheit → z.B. "5 Personen"
    const numeric = (value || "").replace(/[^\d.,]/g, "");
    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          value={numeric}
          placeholder="0"
          onChange={(e) => {
            const v = (e.target.value || "").trim();
            onChange(v ? (unit ? `${v} ${unit}` : v) : "");
          }}
          data-testid={`tool-answer-${index}-number`}
          className="w-32 text-sm rounded-sm border border-ink-300 bg-warm-50 px-3 py-2 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
        />
        {unit && <span className="text-sm text-warm-600">{unit}</span>}
      </div>
    );
  }

  if (type === "short_text") {
    return (
      <input
        type="text"
        value={value || ""}
        maxLength={200}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Ihre Antwort …"}
        data-testid={`tool-answer-${index}`}
        className="w-full text-sm rounded-sm border border-ink-300 bg-warm-50 px-3 py-2 placeholder:text-warm-400 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
      />
    );
  }

  // long_text (Default-Fallback)
  return (
    <textarea
      rows={3}
      maxLength={1000}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || "Ihre Antwort …"}
      data-testid={`tool-answer-${index}`}
      className="w-full text-sm rounded-sm border border-ink-300 bg-warm-50 px-3 py-2 placeholder:text-warm-400 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 resize-y"
    />
  );
}


/**
 * Iter 20.24 — Optionale Freitext-Ergänzung für yes_no / single_choice.
 * Kollabiert per Default. Wenn der User „Antwort ergänzen" klickt, öffnet
 * sich eine kleine Textarea; der Text wird zusammen mit der Chip-Auswahl
 * per Delimiter im Parent-Value gespeichert.
 *
 * Wenn beim Mount schon eine Notiz da ist (z.B. Reload einer Session),
 * wird die Textarea automatisch expandiert.
 */
function FreeTextAppendix({ index, value, onChange }) {
  const { main, note } = splitValue(value);
  const [open, setOpen] = useState(Boolean(note));

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`tool-answer-${index}-add-note-btn`}
        className="mt-2 inline-flex items-center gap-1 text-[11px] text-warm-500 hover:text-brand-600 hover:underline"
      >
        <Plus size={11} weight="bold" />
        Antwort ergänzen (optional)
      </button>
    );
  }

  return (
    <div className="mt-2" data-testid={`tool-answer-${index}-note-wrapper`}>
      <div className="flex items-center justify-between mb-1">
        <label
          htmlFor={`tool-answer-${index}-note`}
          className="text-[11px] text-warm-500"
        >
          Antwort-Ergänzung (optional)
        </label>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onChange(joinValue(main, ""));
          }}
          className="inline-flex items-center gap-0.5 text-[11px] text-warm-400 hover:text-ink-700"
          data-testid={`tool-answer-${index}-note-close`}
          title="Ergänzung entfernen"
        >
          <X size={11} weight="bold" />
        </button>
      </div>
      <textarea
        id={`tool-answer-${index}-note`}
        rows={2}
        maxLength={500}
        value={note}
        onChange={(e) => onChange(joinValue(main, e.target.value))}
        placeholder="z.B. Kontext-Detail, das die Chip-Auswahl präzisiert …"
        data-testid={`tool-answer-${index}-note`}
        className="w-full text-sm rounded-sm border border-ink-200 bg-warm-50/50 px-3 py-2 placeholder:text-warm-400 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 resize-y"
      />
    </div>
  );
}
